import { useCallback, useRef } from "react";

import { useDispatch } from "react-redux";

import { useRequestWithFeedback } from "../../../composites";
import { useObjectMemo } from "../../../hooks";
import { useLibrary } from "../../../providers";

import API from "./api";
import { actions } from "./slice";
import { useQuickActionsCatalog } from "./hooks";

import type {
  FetchQuickActionsParams,
  PostQuickActionsParams,
  FetchQuickActionsCatalogParams,
} from "./types";

export const useFetchQuickActions = (): FetchQuickActionsParams => {
  const dispatch = useDispatch();
  const { throwToast } = useLibrary("toasts");
  const { send, loading, status } =
    useRequestWithFeedback<API.QuickActionsResponse>();

  const fetch = useCallback(async () => {
    try {
      await send({
        action: API.fetch(),
        onData: (data) => {
          dispatch(actions.setAll(data.actions));
        },
      });
    } catch (err) {
      throwToast({
        kind: "error",
        message: "Failed to load quick actions. Please try again.",
      });
    }
  }, [dispatch, send, throwToast]);

  return useObjectMemo({
    send: fetch,
    loading,
    error: status === "error",
  });
};

export const useFetchQuickActionsCatalog =
  (): FetchQuickActionsCatalogParams => {
    const dispatch = useDispatch();
    const { throwToast } = useLibrary("toasts");
    const { send, loading, status } =
      useRequestWithFeedback<API.QuickActionsCatalogResponse>();

    const fetch = useCallback(async () => {
      try {
        await send({
          action: API.fetchCatalog(),
          onData: (data) => {
            dispatch(actions.setCatalog(data.catalog));
          },
        });
      } catch (err) {
        throwToast({
          kind: "error",
          message: "Failed to load quick actions catalog. Please try again.",
        });
      }
    }, [dispatch, send, throwToast]);

    return useObjectMemo({
      send: fetch,
      loading,
      error: status === "error",
    });
  };

export const usePostQuickActions = (): PostQuickActionsParams => {
  const dispatch = useDispatch();
  const catalog = useQuickActionsCatalog();
  const catalogRef = useRef(catalog);
  const { throwToast } = useLibrary("toasts");
  const { send, loading, status } = useRequestWithFeedback<void>();

  // Keep ref updated with latest catalog
  catalogRef.current = catalog;
  const post = useCallback(
    async (actionKeys: string[]) => {
      try {
        await send({
          action: API.post(actionKeys),
          onSuccess: () => {
            // Use the ref to get the most current catalog at the time of success
            const currentCatalog = catalogRef.current;
            // Create QuickActionWithPosition objects from the posted keys
            const updatedActions: API.QuickActionWithPosition[] = actionKeys
              .map((key, index) => {
                const catalogItem = currentCatalog.find(
                  (item: API.QuickActionItem) => item.key === key,
                );
                if (!catalogItem) return null;

                return {
                  action: catalogItem,
                  position: index,
                };
              })
              .filter(
                (action): action is API.QuickActionWithPosition =>
                  action !== null,
              );

            // Dispatch to update the quick actions slice
            dispatch(actions.setAll(updatedActions));
          },
        });
      } catch (err) {
        throwToast({
          kind: "error",
          message: "Failed to save quick actions. Please try again.",
        });
      }
    },
    [send, throwToast, dispatch, catalogRef],
  );

  return useObjectMemo({
    send: post,
    loading,
    error: status === "error",
  });
};
